// frontend-repo/src/components/WelcomeScreen.js
import { Box, Database, Layers, Shuffle, Sparkles, TrendingUp, Zap } from "lucide-react";
import { useState } from "react";

function WelcomeScreen({ onSuggestionClick, backendContext, processContext, user }) {
  const [activeCategory, setActiveCategory] = useState("models");

  const categories = [
    { id: "models", label: "Models", icon: Box, description: "Name generation algorithms" },
    { id: "mutations", label: "Mutations", icon: Shuffle, description: "Name transformations" },
    { id: "pipeline", label: "Pipeline", icon: Layers, description: "How it all works" },
    { id: "metrics", label: "Metrics", icon: TrendingUp, description: "Grading & scoring" },
  ];

  // Dynamic stats from processContext
  const stats = processContext ? {
    models: processContext.models?.length || 0,
    mutations: processContext.mutations?.length || 0,
    totalNames: processContext.globalConfig?.total_names || 1000,
    categories: Object.keys(processContext.modelCategories || {}).length,
  } : null;

  const getSuggestions = category => {
    const contextSuggestions = {
      models: [
        { text: "List all the models", description: `${stats?.models || 'all'} generation algorithms` },
        { text: "Which models produce the most names?", description: "Proportions & output amounts" },
        { text: "What model categories exist?", description: `${stats?.categories || 'multiple'} categories` },
        { text: "Tell me about Coined Names", description: "Made-up words that sound real" },
        { text: "How do metaphor models work?", description: "Nature, animal, food metaphors" },
        { text: "What's the difference between evocative and semantic models?", description: "Emotional vs meaning-based" },
      ],
      mutations: [
        { text: "List all the mutations", description: `${stats?.mutations || 'all'} transformation types` },
        { text: "What's the difference between HEAVY and LIGHT mutations?", description: "Mutation intensity levels" },
        { text: "What mutations apply to coined names?", description: "Model-specific mutations" },
        { text: "How does letter substitution work?", description: "C→K, PH→F transformations" },
        { text: "What is syllable remix?", description: "Breaking words into phonemes" },
        { text: "Give examples of vowel removal", description: "GALAXY → GLXY" },
      ],
      pipeline: [
        { text: "How does Monika generate names?", description: "End-to-end pipeline" },
        { text: "What inputs does a project need?", description: "Description, attributes, countries" },
        { text: "How many names are generated per project?", description: `Target: ${stats?.totalNames || 1000}` },
        { text: "What happens after name generation?", description: "Metrics & mutations" },
        { text: "How are mutations selected?", description: "Trademark grade determines type" },
        { text: "What is language analysis?", description: "Linguistic personality fit" },
      ],
      metrics: [
        { text: "How are names graded?", description: "A-F scoring system" },
        { text: "What metrics are calculated?", description: "Trademark, uniqueness, pronounceability" },
        { text: "How does trademark checking work?", description: "TMView integration" },
        { text: "What makes a name get an A grade?", description: "All metrics must be strong" },
        { text: "Why do F-graded names get heavy mutations?", description: "Aggressive changes needed" },
        { text: "How is pronounceability measured?", description: "Phonetic analysis" },
      ],
    };

    return contextSuggestions[category] || contextSuggestions.models;
  };

  const suggestions = getSuggestions(activeCategory);

  return (
    <div className="welcome-screen">
      <div className="welcome-content">
        {/* Header with sparkle */}
        <div className="welcome-header">
          <Sparkles className="welcome-sparkle" size={28} />
          <h1 className="welcome-title">How can I help you, {user?.displayName?.split(" ")[0] || "there"}?</h1>
        </div>

        {/* Quick stats bar */}
        {stats ? (
          <div className="welcome-stats">
            <div className="welcome-stat">
              <Box size={14} />
              <span>{stats.models} Models</span>
            </div>
            <div className="welcome-stat-divider" />
            <div className="welcome-stat">
              <Shuffle size={14} />
              <span>{stats.mutations} Mutations</span>
            </div>
            <div className="welcome-stat-divider" />
            <div className="welcome-stat">
              <Database size={14} />
              <span>{stats.categories} Categories</span>
            </div>
            <div className="welcome-stat-divider" />
            <div className="welcome-stat">
              <Zap size={14} />
              <span>{stats.totalNames} Names/Project</span>
            </div>
          </div>
        ) : (
          <div className="welcome-stats welcome-stats-skeleton">
            <div className="welcome-stat">
              <div className="skeleton-icon" />
              <div className="skeleton-text" style={{ width: '70px' }} />
            </div>
            <div className="welcome-stat-divider" />
            <div className="welcome-stat">
              <div className="skeleton-icon" />
              <div className="skeleton-text" style={{ width: '85px' }} />
            </div>
            <div className="welcome-stat-divider" />
            <div className="welcome-stat">
              <div className="skeleton-icon" />
              <div className="skeleton-text" style={{ width: '80px' }} />
            </div>
            <div className="welcome-stat-divider" />
            <div className="welcome-stat">
              <div className="skeleton-icon" />
              <div className="skeleton-text" style={{ width: '100px' }} />
            </div>
          </div>
        )}

        {/* Category tabs */}
        <div className="welcome-categories">
          {categories.map(category => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`welcome-category-button ${activeCategory === category.id ? "active" : ""}`}
              >
                <Icon size={16} />
                <div className="welcome-category-text">
                  <span className="welcome-category-label">{category.label}</span>
                  <span className="welcome-category-description">{category.description}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Suggestions grid */}
        <div className="welcome-suggestions">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => onSuggestionClick(suggestion.text)}
              className="welcome-suggestion-button"
            >
              <div className="suggestion-content">
                <span className="suggestion-text">{suggestion.text}</span>
                <span className="suggestion-description">{suggestion.description}</span>
              </div>
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}

export default WelcomeScreen;
